import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { getAvailableSkills } from '../../api/skillsApi';
import { agentApi } from '../../api/agentApi';
import SkillCard from './SkillCard';
const AgentSkillsTab = ({ agentId, currentSkills, onSkillsUpdated, }) => {
    const [availableSkills, setAvailableSkills] = useState([]);
    const [assignedSkillNames, setAssignedSkillNames] = useState(currentSkills);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [draggedSkill, setDraggedSkill] = useState(null);
    const hasChanges = JSON.stringify(assignedSkillNames.sort()) !== JSON.stringify(currentSkills.sort());
    useEffect(() => {
        loadSkills();
    }, []);
    useEffect(() => {
        setAssignedSkillNames(currentSkills);
    }, [currentSkills]);
    const loadSkills = async () => {
        try {
            setLoading(true);
            const skills = await getAvailableSkills();
            setAvailableSkills(skills);
        }
        catch (error) {
            console.error('Failed to load skills:', error);
            // toast.error('Failed to load available skills');
        }
        finally {
            setLoading(false);
        }
    };
    const handleDragStart = (skillName) => {
        setDraggedSkill(skillName);
    };
    const handleDragEnd = () => {
        setDraggedSkill(null);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
    };
    const handleDropToAssigned = (e) => {
        e.preventDefault();
        if (draggedSkill && !assignedSkillNames.includes(draggedSkill)) {
            setAssignedSkillNames([...assignedSkillNames, draggedSkill]);
        }
        setDraggedSkill(null);
    };
    const handleDropToAvailable = (e) => {
        e.preventDefault();
        if (draggedSkill && assignedSkillNames.includes(draggedSkill)) {
            setAssignedSkillNames(assignedSkillNames.filter((s) => s !== draggedSkill));
        }
        setDraggedSkill(null);
    };
    const handleSave = async () => {
        try {
            setSaving(true);
            await agentApi.updateAgent(agentId, { skills: assignedSkillNames });
            // toast.success('Agent skills updated successfully');
            if (onSkillsUpdated) {
                onSkillsUpdated(assignedSkillNames);
            }
        }
        catch (error) {
            console.error('Failed to update agent skills:', error);
            // toast.error('Failed to update agent skills');
        }
        finally {
            setSaving(false);
        }
    };
    const handleCancel = () => {
        setAssignedSkillNames(currentSkills);
    };
    const filteredAvailableSkills = availableSkills.filter((skill) => !assignedSkillNames.includes(skill.name) &&
        (searchTerm === '' ||
            skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            skill.description.toLowerCase().includes(searchTerm.toLowerCase())));
    const assignedSkills = availableSkills.filter((skill) => assignedSkillNames.includes(skill.name));
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" }) }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4", children: _jsx("p", { className: "text-sm text-blue-800", children: "Drag skills between panels to assign or unassign them from this agent. Skills provide tools and capabilities that the agent can use during task execution." }) }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-2", children: "Available Skills" }), _jsx("input", { type: "text", placeholder: "Search skills...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), className: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" })] }), _jsx("div", { className: "border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[400px] max-h-[600px] overflow-y-auto bg-gray-50", onDragOver: handleDragOver, onDrop: handleDropToAvailable, children: filteredAvailableSkills.length === 0 ? (_jsx("p", { className: "text-center text-gray-500 py-8", children: searchTerm ? 'No skills match your search' : 'All skills are assigned' })) : (_jsx("div", { className: "space-y-2", children: filteredAvailableSkills.map((skill) => (_jsx("div", { onDragStart: () => handleDragStart(skill.name), onDragEnd: handleDragEnd, children: _jsx(SkillCard, { skill: skill }) }, skill.name))) })) })] }), _jsxs("div", { className: "space-y-3", children: [_jsxs("h3", { className: "text-lg font-semibold text-gray-900", children: ["Assigned Skills (", assignedSkills.length, ")"] }), _jsx("div", { className: "border-2 border-dashed border-blue-300 rounded-lg p-4 min-h-[400px] max-h-[600px] overflow-y-auto bg-blue-50", onDragOver: handleDragOver, onDrop: handleDropToAssigned, children: assignedSkills.length === 0 ? (_jsx("p", { className: "text-center text-gray-500 py-8", children: "No skills assigned. Drag skills from the left panel to assign them." })) : (_jsx("div", { className: "space-y-2", children: assignedSkills.map((skill) => (_jsx("div", { onDragStart: () => handleDragStart(skill.name), onDragEnd: handleDragEnd, children: _jsx(SkillCard, { skill: skill }) }, skill.name))) })) })] })] }), _jsxs("div", { className: "flex justify-end space-x-3 pt-4 border-t", children: [_jsx("button", { onClick: handleCancel, disabled: !hasChanges || saving, className: "px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed", children: "Cancel" }), _jsx("button", { onClick: handleSave, disabled: !hasChanges || saving, className: "px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center", children: saving ? (_jsxs(_Fragment, { children: [_jsxs("svg", { className: "animate-spin -ml-1 mr-2 h-4 w-4 text-white", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Saving..."] })) : ('Save Changes') })] })] }));
};
export default AgentSkillsTab;
